/**
 * Remark plugin that strips leftover leaked HTML-like markup tags from chat
 * output (PLT-5092).
 *
 * MUST run LAST in the remark plugin list — after `remarkToneBadges`,
 * `remarkCitationPills`, `remarkLockBadges`, and `remarkInlineEmphasis` have
 * each consumed their supported directive (`<badge>`, `<cite>`, `<private />`,
 * `<b>`/`<strong>`/`<i>`/`<em>`). By then every well-formed supported tag is a
 * non-`html` node, so any remaining bare-tag `html` node is an unhandled leak
 * (a `<bold>` typo, a stray `<section>`, a malformed unclosed `<badge …>`). We
 * drop those leftover nodes; their inner text lives in sibling `text` nodes and
 * is preserved, so `<bold>Lorem</bold>` renders as `Lorem`.
 *
 * Used only on the non-team rendering path — team users keep seeing the leaked
 * tags so drift stays visible for debugging. See `upright-chat-markdown.tsx`
 * for the team / non-team plugin-list split.
 *
 * See `strip-leaked-markup-tags.ts` for the pure, unit-tested transform — this
 * plugin is a thin `unist-util-visit` wrapper that walks every container node
 * and rebuilds its children, mirroring the second pass in `remarkInlineEmphasis`.
 */
import { visit } from 'unist-util-visit';
import type { Plugin } from 'unified';
import type { Root } from 'mdast';
import { stripLeakedMarkupTagNodes, type StripReconNode } from './strip-leaked-markup-tags';

export const remarkStripLeakedMarkupTags: Plugin<[], Root> = () => {
  return tree => {
    // Visit every node and rebuild its children array in place when a leaked
    // bare-tag html node is found. Rebuilding is safe to re-run — a children
    // array with no leaked tags returns null (no-op).
    visit(tree, node => {
      const withChildren = node as unknown as { children?: unknown };
      if (!Array.isArray(withChildren.children)) return;
      const rebuilt = stripLeakedMarkupTagNodes(withChildren.children as StripReconNode[]);
      if (rebuilt === null) return;
      withChildren.children = rebuilt;
    });
  };
};
